import { useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import {
  useGenerationAttempt,
  useDeleteGenerationAttempt,
  type GenerationAttemptErrorLog,
} from '../api/admin-api'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { LayoutTemplate, Trash2, ChevronLeft, ChevronRight, X } from 'lucide-react'
import { toast } from 'sonner'
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
} from '@/components/ui/breadcrumb'

const STAGE_LABEL_MAP: Record<GenerationAttemptErrorLog['stage'], string> = {
  'reference-download': '참조 이미지 다운로드',
  'image-generation': 'AI 생성',
  's3-upload': 'S3 업로드',
  'reference-copy': '참조 이미지 복사',
  'db-insert': 'DB 저장',
  'unexpected': '기타',
}

function formatDateTime(value: string): string {
  return new Date(value).toLocaleString('ko-KR')
}

export function FailedImageDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { data: attempt, isLoading, error } = useGenerationAttempt(id!)
  const deleteMutation = useDeleteGenerationAttempt()
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  const [generatedLightboxIndex, setGeneratedLightboxIndex] = useState<number | null>(null)

  const handleDelete = async () => {
    if (!id) return

    try {
      await deleteMutation.mutateAsync(id)
      toast.success('실패 이력이 삭제되었습니다')
      navigate('/admin/failed-images')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '삭제에 실패했습니다')
    }
  }

  if (isLoading) {
    return <div className="text-center py-12 text-gray-500">로딩 중...</div>
  }

  if (error) {
    return <div className="text-center py-12 text-red-500">에러: {error.message}</div>
  }

  if (!attempt) return null

  return (
    <div className="max-w-5xl space-y-6">
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/admin">어드민</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/admin/failed-images">실패한 이미지</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>상세</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-heading tracking-tight">실패한 이미지 상세</h1>
          <p className="text-caption mt-2 leading-relaxed">
            실패한 생성 요청의 입력값과 에러 로그를 확인합니다
          </p>
        </div>
        <Button variant="destructive" onClick={() => setShowDeleteDialog(true)}>
          <Trash2 className="w-4 h-4 mr-2" />
          삭제
        </Button>
      </div>

      <div className="bg-white rounded-lg border p-6 space-y-6">
        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="text-sm font-medium text-gray-700">시도 ID</label>
            <div className="mt-1 text-gray-900 font-mono text-sm">{attempt.id}</div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Generation ID</label>
            <div className="mt-1 text-gray-900 font-mono text-sm">{attempt.generationId}</div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">사용자</label>
            <div className="mt-1 text-gray-900">{attempt.user?.email ?? '-'}</div>
            <div className="text-xs text-gray-400 mt-1 font-mono">{attempt.userId}</div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">모델</label>
            <div className="mt-1 text-gray-900">{attempt.modelId}</div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">스타일</label>
            <div className="mt-1 text-gray-900">{attempt.style || '-'}</div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">비율</label>
            <div className="mt-1 text-gray-900">{attempt.aspectRatio}</div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">요청/성공/실패</label>
            <div className="mt-1 flex items-center gap-2 text-sm">
              <Badge variant="outline">요청 {attempt.requestedCount}</Badge>
              <Badge variant="default">성공 {attempt.successCount}</Badge>
              <Badge variant="destructive">실패 {attempt.failureCount}</Badge>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">생성일시</label>
            <div className="mt-1 text-gray-900 text-sm">{formatDateTime(attempt.createdAt)}</div>
          </div>

          <div className="col-span-2">
            <label className="text-sm font-medium text-gray-700">템플릿</label>
            {attempt.template ? (
              <div className="mt-2 flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                {attempt.template.thumbnailUrl ? (
                  <img
                    src={attempt.template.thumbnailUrl}
                    alt={attempt.template.name}
                    className="w-16 h-16 rounded-lg object-cover border"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-lg bg-gray-200 flex items-center justify-center">
                    <LayoutTemplate className="w-6 h-6 text-gray-400" />
                  </div>
                )}
                <div>
                  <div className="font-medium text-gray-900">{attempt.template.name}</div>
                  {attempt.template.description && (
                    <p className="text-sm text-gray-500 mt-0.5">{attempt.template.description}</p>
                  )}
                </div>
              </div>
            ) : (
              <div className="mt-1 text-gray-400">사용된 템플릿 없음</div>
            )}
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700">프롬프트</label>
          <div className="mt-1 p-4 bg-gray-50 rounded-lg text-gray-900 whitespace-pre-wrap font-mono text-sm">
            {attempt.prompt}
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700">에러 요약</label>
          <div className="mt-1 p-4 bg-red-50 border border-red-100 rounded-lg text-red-700 text-sm">
            {attempt.errorSummary}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border p-6">
        <label className="text-sm font-medium text-gray-700 block mb-4">
          에러 로그 ({attempt.errorLogs.length}개)
        </label>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px]">
            <thead className="bg-gray-50 border-y border-gray-200">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">단계</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">메시지</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">시도 정보</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">시각</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {attempt.errorLogs.map((log, idx) => (
                <tr key={`${log.loggedAt}-${idx}`}>
                  <td className="px-4 py-3">
                    <Badge variant="destructive" className="min-w-[3rem] justify-center whitespace-nowrap">
                      {STAGE_LABEL_MAP[log.stage]}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-800">{log.message}</td>
                  <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">
                    {log.imageIndex !== undefined ? `이미지 ${log.imageIndex + 1}` : '-'}
                    {log.attempt ? ` / ${log.attempt}회차` : ''}
                    {log.maxRetries ? ` (최대 ${log.maxRetries})` : ''}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">
                    {formatDateTime(log.loggedAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {attempt.referenceImages && attempt.referenceImages.length > 0 && (
        <div className="bg-white rounded-lg border p-6">
          <label className="text-sm font-medium text-gray-700 block mb-4">
            참조 이미지 ({attempt.referenceImages.length}개)
          </label>
          <div className="grid grid-cols-4 gap-4">
            {attempt.referenceImages.map((ref, idx) => (
              <div
                key={idx}
                className="bg-gray-100 rounded-lg overflow-hidden cursor-pointer ring-offset-2 hover:ring-2 hover:ring-primary/40 transition-shadow"
                onClick={() => setLightboxIndex(idx)}
              >
                <img
                  src={ref.thumbnailUrl}
                  alt={`참조 이미지 ${idx + 1}`}
                  className="w-full h-auto object-contain"
                  loading="lazy"
                />
              </div>
            ))}
          </div>

          {/* Reference Image Lightbox */}
          <Dialog
            open={lightboxIndex !== null}
            onOpenChange={(open) => {
              if (!open) setLightboxIndex(null)
            }}
          >
            <DialogContent className="max-w-3xl p-0 border-0 bg-transparent shadow-none [&>button]:hidden">
              <DialogTitle className="sr-only">참조 이미지 미리보기</DialogTitle>
              {lightboxIndex !== null && attempt.referenceImages && (
                <div className="relative flex items-center justify-center">
                  <img
                    src={attempt.referenceImages[lightboxIndex].imageUrl}
                    alt={`참조 이미지 ${lightboxIndex + 1}`}
                    className="max-h-[80vh] w-auto rounded-lg object-contain"
                  />

                  {/* Close */}
                  <button
                    onClick={() => setLightboxIndex(null)}
                    className="absolute top-2 right-2 rounded-full bg-black/60 p-1.5 text-white hover:bg-black/80 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>

                  {/* Prev */}
                  {attempt.referenceImages.length > 1 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setLightboxIndex(
                          (lightboxIndex - 1 + attempt.referenceImages!.length) %
                            attempt.referenceImages!.length,
                        )
                      }}
                      className="absolute left-2 rounded-full bg-black/60 p-1.5 text-white hover:bg-black/80 transition-colors"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                  )}

                  {/* Next */}
                  {attempt.referenceImages.length > 1 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setLightboxIndex(
                          (lightboxIndex + 1) % attempt.referenceImages!.length,
                        )
                      }}
                      className="absolute right-2 rounded-full bg-black/60 p-1.5 text-white hover:bg-black/80 transition-colors"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  )}

                  {/* Counter */}
                  {attempt.referenceImages.length > 1 && (
                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-3 py-1 text-xs text-white">
                      {lightboxIndex + 1} / {attempt.referenceImages.length}
                    </div>
                  )}
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>
      )}

      {attempt.generatedImages.length > 0 && (
        <div className="bg-white rounded-lg border p-6">
          <label className="text-sm font-medium text-gray-700 block mb-4">
            생성된 이미지 ({attempt.generatedImages.length}개)
          </label>
          <div className="grid grid-cols-2 gap-4">
            {attempt.generatedImages.map((img, idx) => (
              <div key={idx} className="space-y-2">
                <img
                  src={img.imageUrl}
                  alt={`생성된 이미지 ${idx + 1}`}
                  className="w-full rounded-lg border shadow-sm cursor-pointer ring-offset-2 hover:ring-2 hover:ring-primary/40 transition-shadow"
                  loading="lazy"
                  onClick={() => setGeneratedLightboxIndex(idx)}
                />
                <div className="text-xs text-gray-500 font-mono text-center">seed: {img.seed}</div>
              </div>
            ))}
          </div>

          {/* Generated Image Lightbox */}
          <Dialog
            open={generatedLightboxIndex !== null}
            onOpenChange={(open) => {
              if (!open) setGeneratedLightboxIndex(null)
            }}
          >
            <DialogContent className="max-w-3xl p-0 border-0 bg-transparent shadow-none [&>button]:hidden">
              <DialogTitle className="sr-only">생성된 이미지 미리보기</DialogTitle>
              {generatedLightboxIndex !== null && attempt.generatedImages && (
                <div className="relative flex items-center justify-center">
                  <img
                    src={attempt.generatedImages[generatedLightboxIndex].imageUrl}
                    alt={`생성된 이미지 ${generatedLightboxIndex + 1}`}
                    className="max-h-[80vh] w-auto rounded-lg object-contain"
                  />

                  {/* Close */}
                  <button
                    onClick={() => setGeneratedLightboxIndex(null)}
                    className="absolute top-2 right-2 rounded-full bg-black/60 p-1.5 text-white hover:bg-black/80 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>

                  {/* Prev */}
                  {attempt.generatedImages.length > 1 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setGeneratedLightboxIndex(
                          (generatedLightboxIndex - 1 + attempt.generatedImages!.length) %
                            attempt.generatedImages!.length,
                        )
                      }}
                      className="absolute left-2 rounded-full bg-black/60 p-1.5 text-white hover:bg-black/80 transition-colors"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                  )}

                  {/* Next */}
                  {attempt.generatedImages.length > 1 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setGeneratedLightboxIndex(
                          (generatedLightboxIndex + 1) % attempt.generatedImages!.length,
                        )
                      }}
                      className="absolute right-2 rounded-full bg-black/60 p-1.5 text-white hover:bg-black/80 transition-colors"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  )}

                  {/* Counter */}
                  {attempt.generatedImages.length > 1 && (
                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-3 py-1 text-xs text-white">
                      {generatedLightboxIndex + 1} / {attempt.generatedImages.length}
                    </div>
                  )}
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>
      )}

      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>실패 이력 삭제</DialogTitle>
            <DialogDescription className="mt-2">
              이 실패 이력을 삭제하시겠습니까? 삭제 후 복구할 수 없습니다.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              disabled={deleteMutation.isPending}
            >
              취소
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? '삭제 중...' : '삭제'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
